import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { MotionConfig } from "framer-motion";
import { IdleSignOut } from "@/components/security/IdleSignOut";
import { SebStart, SebQuit } from "@/pages/SebStart";
import { LoadingState } from "@/components/shared";

import AssessmentInvite from "@/pages/AssessmentInvite";
import Login from "@/pages/Login";
import SocialCallback from "@/pages/SocialCallback";
import Account from "@/pages/Account";
import Landing from "@/pages/Landing";
import Onboarding from "@/pages/candidate/Onboarding";
import RecruiterHome from "@/pages/recruiter/Home";
import Workspace from "@/pages/recruiter/Workspace";
import Jobs from "@/pages/recruiter/Jobs";
import JobBuilder from "@/pages/recruiter/JobBuilder";
import Pipeline from "@/pages/recruiter/Pipeline";
import CandidateHome from "@/pages/candidate/Home";
import CandidateAIHome from "@/pages/candidate/AIHome";
import CandidateIntelligence from "@/pages/recruiter/CandidateIntelligence";
import Compare from "@/pages/recruiter/Compare";
import CandidateProfile from "@/pages/candidate/Profile";
import CandidateJobs from "@/pages/candidate/Jobs";
import AutoApply from "@/pages/candidate/AutoApply";
import TestStudio from "@/pages/recruiter/TestStudio";
import CandidateApply from "@/pages/candidate/Apply";
import ExamRoom from "@/pages/candidate/ExamRoom";
import QBank from "@/pages/recruiter/QBank";
import TrialReport from "@/pages/recruiter/TrialReport";
import Admin from "@/pages/admin/Admin";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import InviteAccept from "@/pages/InviteAccept";
import Interviews from "@/pages/recruiter/Interviews";
import Analytics from "@/pages/recruiter/Analytics";
import Copilot from "@/pages/Copilot";
import { CareersList, CareersJob, CareersPreview } from "@/pages/Careers";
import OrgCareers from "@/pages/OrgCareers";
import PublicOnboarding from "@/pages/PublicOnboarding";
import RecruiterStart from "@/pages/RecruiterStart";
import Verify from "@/pages/Verify";
import VerifyEmail from "@/pages/VerifyEmail";
import ReplyToVerify from "@/pages/ReplyToVerify";
import SharedProfile from "@/pages/SharedProfile";
import { CandidateGate } from "@/components/CandidateGate";

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const to = user.role === "super_admin" ? "/superadmin" : user.role === "admin" ? "/admin" : user.role === "recruiter" ? "/recruiter" : "/candidate";
  return <Navigate to={to} replace />;
}

const LEGACY_TABS = { jd: "build", assessment: "test", screening: "screening", candidates: "screening", analytics: "screening" };
function LegacyWorkspaceRedirect() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const section = LEGACY_TABS[(params.get("tab") || "").toLowerCase()];
  return <Navigate to={`/recruiter/jobs/${jobId}${section ? `/${section}` : ""}`} replace />;
}

function LandingOrHome() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <RoleRedirect />;
  return <Landing />;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingState label="Loading workspace…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "super_admin") return <RoleRedirect />;
  return <AppShell>{children}</AppShell>;
}

function ProtectedBare({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingState label="Loading…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "super_admin") return <RoleRedirect />;
  return children;
}

function ProtectedGov({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingState label="Loading…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = ["admin", "super_admin"].includes(user.role) || (user.role === "recruiter" && user.org_role === "owner");
  if (!ok) return <RoleRedirect />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <div className="App">
      <MotionConfig reducedMotion="user">
      <AuthProvider>
        <BrowserRouter>
          <IdleSignOut />
          <Routes>
            <Route path="/assessment-invite/:jobId" element={<AssessmentInvite />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/social" element={<SocialCallback />} />
            <Route path="/careers" element={<CareersList />} />
            <Route path="/careers/org/:slug" element={<OrgCareers />} />
            <Route path="/careers/:jobId" element={<CareersJob />} />
            <Route path="/preview/:token" element={<CareersPreview />} />
            <Route path="/onboarding" element={<PublicOnboarding />} />
            <Route path="/get-started/recruiter" element={<RecruiterStart />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/seb/start/:token" element={<SebStart />} />
            <Route path="/seb/quit" element={<SebQuit />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-reply-to" element={<ReplyToVerify />} />
            <Route path="/shared/:token" element={<SharedProfile />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/" element={<LandingOrHome />} />
            <Route path="/copilot" element={<Protected roles={["recruiter", "candidate", "admin", "super_admin"]}><Copilot /></Protected>} />
            <Route path="/copilot/:sessionId" element={<Protected roles={["recruiter", "candidate", "admin", "super_admin"]}><Copilot /></Protected>} />
            <Route path="/account" element={<Protected roles={["recruiter", "candidate", "admin", "super_admin"]}><Account /></Protected>} />

            <Route path="/recruiter" element={<Protected roles={["recruiter", "admin"]}><RecruiterHome /></Protected>} />
            <Route path="/recruiter/dashboard" element={<Navigate to="/recruiter/analytics" replace />} />
            <Route path="/recruiter/analytics" element={<Protected roles={["recruiter", "admin"]}><Analytics /></Protected>} />
            <Route path="/recruiter/interviews" element={<Protected roles={["recruiter", "admin"]}><Interviews /></Protected>} />
            <Route path="/recruiter/workspace/:jobId" element={<LegacyWorkspaceRedirect />} />
            <Route path="/recruiter/jobs" element={<Protected roles={["recruiter", "admin"]}><Jobs /></Protected>} />
            <Route path="/recruiter/jobs/:jobId" element={<Protected roles={["recruiter", "admin"]}><Workspace /></Protected>} />
            <Route path="/recruiter/jobs/:jobId/pipeline" element={<Protected roles={["recruiter", "admin"]}><Pipeline /></Protected>} />
            <Route path="/recruiter/jobs/:jobId/:section" element={<Protected roles={["recruiter", "admin"]}><Workspace /></Protected>} />
            <Route path="/recruiter/jobs/:jobId/trial/:trialId" element={<ProtectedBare roles={["recruiter", "admin"]}><ExamRoom /></ProtectedBare>} />
            <Route path="/recruiter/qbank" element={<Protected roles={["recruiter", "admin"]}><QBank /></Protected>} />
            <Route path="/recruiter/trial-report/:trialId" element={<Protected roles={["recruiter", "admin"]}><TrialReport /></Protected>} />
            <Route path="/recruiter/pipeline" element={<Protected roles={["recruiter", "admin"]}><Pipeline /></Protected>} />
            <Route path="/recruiter/candidate/:candId" element={<Protected roles={["recruiter", "admin"]}><CandidateIntelligence /></Protected>} />
            <Route path="/recruiter/compare/:jobId" element={<Protected roles={["recruiter", "admin"]}><Compare /></Protected>} />

            <Route path="/candidate" element={<Protected roles={["candidate"]}><CandidateGate><CandidateAIHome /></CandidateGate></Protected>} />
            <Route path="/candidate/onboarding" element={<Protected roles={["candidate"]}><Onboarding /></Protected>} />
            <Route path="/candidate/applications" element={<Protected roles={["candidate"]}><CandidateGate><CandidateHome /></CandidateGate></Protected>} />
            <Route path="/candidate/profile" element={<Protected roles={["candidate"]}><CandidateProfile /></Protected>} />
            <Route path="/candidate/jobs" element={<Protected roles={["candidate"]}><CandidateGate><CandidateJobs /></CandidateGate></Protected>} />
            <Route path="/candidate/auto-apply" element={<Protected roles={["candidate"]}><CandidateGate><AutoApply /></CandidateGate></Protected>} />
            <Route path="/candidate/apply/:jobId" element={<Protected roles={["candidate"]}><CandidateGate><CandidateApply /></CandidateGate></Protected>} />
            <Route path="/candidate/exam/:candId" element={<ProtectedBare roles={["candidate"]}><ExamRoom /></ProtectedBare>} />

            <Route path="/admin" element={<ProtectedGov><Admin tab="governance" /></ProtectedGov>} />
            <Route path="/admin/users" element={<ProtectedGov><Admin tab="users" /></ProtectedGov>} />
            <Route path="/admin/audit" element={<ProtectedGov><Admin tab="audit" /></ProtectedGov>} />
            <Route path="/admin/settings" element={<ProtectedGov><Admin tab="settings" /></ProtectedGov>} />
            <Route path="/admin/bias" element={<ProtectedGov><Admin tab="bias" /></ProtectedGov>} />

            <Route path="/superadmin" element={<Protected roles={["super_admin"]}><SuperAdmin /></Protected>} />
            <Route path="/superadmin/overview" element={<Protected roles={["super_admin"]}><SuperAdmin /></Protected>} />

            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </MotionConfig>
    </div>
  );
}

export default App;
